import { formatTicker } from '@api';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { Toggle } from '@design/components/toggle/Toggle';
import { ChangeEvent, useMemo } from 'react';

import { EventsConfig } from '../../../types/events.types';
import styles from './InsightsDropdown.module.scss';
import {
  handleAllInsightsToggle,
  handleAssetToggle,
  isAssetEnabled,
} from './utils';

export interface InsightsDropdownProps {
  assets: {
    tag: string;
    name_short: string;
    name: string;
    ticker: string;
    exchange: { code: string; name: string };
  }[];
  eventsConfig: EventsConfig;
  onChange: (config: EventsConfig) => void;
  locales: {
    showInsights: string;
  };
}

export const InsightsDropdown = ({
  assets,
  eventsConfig,
  onChange,
  locales,
}: InsightsDropdownProps) => {
  const uniqueAssets = useMemo(() => {
    const seen = new Set<string>();
    return assets.filter(asset => {
      if (seen.has(asset.tag)) {
        return false;
      }

      seen.add(asset.tag);
      return true;
    });
  }, [assets]);

  const allInsightsEnabled = eventsConfig.TMI;

  return (
    <div>
      <div className={styles.allInsightsRow}>
        <span className={styles.allInsightsLabel}>{locales.showInsights}</span>
        <Toggle
          isChecked={allInsightsEnabled}
          onToggle={(checked: boolean) =>
            handleAllInsightsToggle({
              checked,
              uniqueAssets,
              eventsConfig,
              onChange,
            })
          }
          size="small"
          data-testid="all-insights-toggle"
        />
      </div>
      <div className={styles.assetList}>
        {uniqueAssets.map(asset => {
          const ticker = formatTicker(asset);
          const entityName = asset.name_short || asset.name;
          return (
            <div className={styles.assetRow} key={asset.tag}>
              <div className={styles.checkboxWrapper}>
                <CheckBox
                  name={asset.tag}
                  label={ticker}
                  checked={isAssetEnabled({
                    tag: asset.tag,
                    eventsConfig,
                  })}
                  onChange={(_, e: ChangeEvent<HTMLInputElement>) =>
                    handleAssetToggle({
                      tag: asset.tag,
                      checked: e.target.checked,
                      eventsConfig,
                      uniqueAssets,
                      onChange,
                    })
                  }
                  size="small"
                  testId={`asset-checkbox-${asset.tag}`}
                />
              </div>
              <span className={styles.entityName}>{entityName}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
